import React, { useState } from 'react';
import { Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { authService } from '../services/authService';

/**
 * Waive Login Screen
 * Clean, professional design with Waive branding
 */
function LoginScreen({ onLoginSuccess }) {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const result = await authService.login(username, password);

            if (result.success) {
                onLoginSuccess(result.user, result.token);
            } else {
                setError(result.message || 'Login failed. Please try again.');
            }
        } catch (err) {
            setError('Unable to connect to server. Please try again.');
            console.error('Login error:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-screen">
            <style>{`
                .login-screen {
                    min-height: 100vh;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: linear-gradient(135deg, #1a365d 0%, #2d5a87 50%, #3d7ab5 100%);
                    padding: 20px;
                }
                
                .login-container {
                    width: 100%;
                    max-width: 420px;
                }
                
                .login-card {
                    border: none;
                    border-radius: 16px;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
                    overflow: hidden;
                }
                
                .login-header {
                    background: linear-gradient(135deg, #0d2137 0%, #1a365d 100%);
                    padding: 40px 30px;
                    text-align: center;
                    color: white;
                }
                
                .login-logo {
                    font-size: 3rem;
                    font-weight: 700;
                    letter-spacing: -1px;
                    margin-bottom: 8px;
                }
                
                .login-logo .logo-accent {
                    color: #4da6ff;
                }
                
                .login-tagline {
                    font-size: 0.95rem;
                    opacity: 0.85;
                    font-weight: 300;
                }
                
                .login-body {
                    padding: 40px 35px;
                    background: #ffffff;
                }
                
                .login-title {
                    font-size: 1.4rem;
                    font-weight: 600;
                    color: #1a365d;
                    margin-bottom: 25px;
                    text-align: center;
                }
                
                .form-floating {
                    margin-bottom: 20px;
                }
                
                .form-floating .form-control {
                    border: 2px solid #e2e8f0;
                    border-radius: 10px;
                    padding: 1rem 1rem;
                    height: auto;
                    font-size: 1rem;
                    transition: all 0.2s ease;
                }
                
                .form-floating .form-control:focus {
                    border-color: #4da6ff;
                    box-shadow: 0 0 0 4px rgba(77, 166, 255, 0.15);
                }
                
                .form-floating label {
                    padding: 1rem;
                    color: #64748b;
                }
                
                .login-btn {
                    width: 100%;
                    padding: 14px;
                    font-size: 1.1rem;
                    font-weight: 600;
                    border-radius: 10px;
                    background: linear-gradient(135deg, #1a365d 0%, #2d5a87 100%);
                    border: none;
                    transition: all 0.3s ease;
                }
                
                .login-btn:hover:not(:disabled) {
                    background: linear-gradient(135deg, #2d5a87 0%, #3d7ab5 100%);
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(26, 54, 93, 0.3);
                }
                
                .login-btn:disabled {
                    opacity: 0.7;
                }
                
                .login-footer {
                    text-align: center;
                    padding: 20px 35px 30px;
                    background: #f8fafc;
                    border-top: 1px solid #e2e8f0;
                }
                
                .login-footer-text {
                    font-size: 0.85rem;
                    color: #64748b;
                    margin: 0;
                }
                
                .login-footer-text a {
                    color: #2d5a87;
                    text-decoration: none;
                    font-weight: 500;
                }
                
                .powered-by {
                    margin-top: 30px;
                    text-align: center;
                    color: rgba(255, 255, 255, 0.6);
                    font-size: 0.85rem;
                }
                
                .powered-by strong {
                    color: rgba(255, 255, 255, 0.9);
                }
                
                .input-icon {
                    position: absolute;
                    right: 15px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #94a3b8;
                    pointer-events: none;
                }
                
                .form-group-icon {
                    position: relative;
                }
                
                .alert {
                    border-radius: 10px;
                    border: none;
                    font-size: 0.95rem;
                }
            `}</style>

            <div className="login-container">
                <Card className="login-card">
                    <div className="login-header">
                        <div className="login-logo">
                            <span className="logo-accent">W</span>aive
                        </div>
                        <div className="login-tagline">
                            Your Financial Advisor
                        </div>
                    </div>

                    <div className="login-body">
                        <h2 className="login-title">
                            <FontAwesomeIcon icon="lock" className="me-2" style={{ color: '#4da6ff' }} />
                            Sign In
                        </h2>

                        {error && (
                            <Alert variant="danger" className="mb-4">
                                <FontAwesomeIcon icon="exclamation-circle" className="me-2" />
                                {error}
                            </Alert>
                        )}

                        <Form onSubmit={handleSubmit}>
                            <Form.Floating className="mb-3">
                                <Form.Control
                                    id="username"
                                    type="text"
                                    placeholder="Username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    required
                                    autoComplete="username"
                                    autoFocus
                                />
                                <label htmlFor="username">
                                    <FontAwesomeIcon icon="user" className="me-2" />
                                    Username
                                </label>
                            </Form.Floating>

                            <Form.Floating className="mb-4">
                                <Form.Control
                                    id="password"
                                    type="password"
                                    placeholder="Password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    autoComplete="current-password"
                                />
                                <label htmlFor="password">
                                    <FontAwesomeIcon icon="key" className="me-2" />
                                    Password
                                </label>
                            </Form.Floating>

                            <Button
                                type="submit"
                                className="login-btn"
                                disabled={loading || !username || !password}
                            >
                                {loading ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Signing in...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon="sign-in-alt" className="me-2" />
                                        Sign In
                                    </>
                                )}
                            </Button>
                        </Form>
                    </div>

                    <div className="login-footer">
                        <p className="login-footer-text">
                            <FontAwesomeIcon icon="shield-alt" className="me-2" />
                            FCA Compliant • Secure 
                        </p>
                    </div>
                </Card>
           </div>
        </div>
    );
}

export default LoginScreen;